// ---------------------------------------------------------------------------
// AppFrame — a casca do app. No mobile é um `<div>` comum; a partir do corte de
// desktop vira uma coluna de largura de celular, centralizada sobre um fundo.
//
// ## Por que existe
//
// O produto não tem desenho de desktop, e não ter desenho não é o mesmo que
// esticar. Um layout validado em 375–430px espalhado por 1440px não fica
// "espaçoso": fica com uma linha de texto de 1400px, capas gigantes e a
// BottomNav atravessando a tela inteira. Enquanto o desktop de verdade não
// chega, a resposta honesta é servir o mock de mobile e limitar a área.
//
// A ideia de "coluna central" já estava registrada em três lugares do repo
// (`AppRoutes`, `AdminShell`, docs) como se fosse um guardrail existente — mas
// nunca houve casca nenhuma: a contenção era `max-w-md` repetido em 49 lugares,
// e 13 páginas não tinham largura alguma. Este arquivo é o guardrail virando
// código.
//
// ## Por que a estrutura é esta, e não um `max-width` simples
//
// Um `max-width` contém o conteúdo em fluxo normal e NÃO contém `position:
// fixed` — que no app são 69 call sites (BottomNav, sheets, overlays). O
// resultado seria a tela partida em duas larguras: conteúdo em 430px, chrome em
// 1440px.
//
// Medido no Chromium a 1440px, quatro arranjos:
//  · sem contenção (hoje) ........... barra 1425px, ancorada  → vaza
//  · frame com `transform` que rola .. barra 415px, ROLA EMBORA (844→244)
//  · frame `position: fixed` ......... barra 1440px            → não contém
//  · casca fixa transformada + scroller interno .. 430px E ancorada ✓
//
// Só o último dá as duas coisas, e a razão é a regra que os outros três
// esbarram: `transform`/`translate` cria containing block para descendente
// `fixed`, mas se essa mesma caixa for quem rola, o descendente passa a rolar
// junto. Por isso quem transforma (a casca) e quem rola (o filho) precisam ser
// elementos diferentes.
//
// Consequências que valem citar, porque não são óbvias e já foram medidas:
//  · a casca vira stacking context — todo z-index de dentro passa a ser
//    relativo a ela, o que isola o app do fundo em vez de competir com ele;
//  · `position: sticky` e `backdrop-filter` de dentro continuam funcionando
//    (medidos, contidos nos 430px);
//  · quem rola deixa de ser o documento, então `window.scrollY`/`scrollTo` são
//    respostas erradas no desktop — ver `lib/layout/appScroll.ts`, que é a
//    autoridade única disso.
//
// ## Fundo
//
// O entorno é `bg-surface-alt`, que neste projeto resolve para
// `var(--border-color)` — a mesma tinta das bordas. Não é acaso aproveitado: a
// cor da borda é, por definição, a que o tema garante distinguível do fundo das
// páginas, então a aresta do frame lê como borda em todos os temas sem uma
// borda existir. Uma `border-line` aqui seria literalmente invisível, porque
// seria a cor do próprio entorno.
//
// ## Casca de desktop (30/09/2026)
//
// A partir de 1024px, na web e com sessão, o frame ganha barra lateral e topo
// (`src/desktop/`, mock `redesign-livra/web-desktop/`). O frame continua o
// mesmo — casca transformada, scroller interno, overlay host — e só muda de
// lugar; a BottomNav some sozinha (`useDesktopShell`). Toda página ainda abre
// na coluna de 430px: os layouts de desktop por página são fatias seguintes.
// ---------------------------------------------------------------------------

import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { useLocation } from "react-router-dom";
import { KeepAliveOutlet } from "../components/layout/KeepAliveOutlet";
import { useAuth } from "../hooks/useAuth";
import {
  DesktopShellContext,
  readDesktopShellEnabled,
  readRealSessionHint,
  shellSessionGuess,
  writeRealSessionHint,
  shouldUseDesktopShell,
  shouldUseVisitorShell,
  type OwnerTheme,
  useWideViewport,
  type DesktopShellValue,
} from "../lib/layout/desktopShell";
import { APP_SCROLL_ROOT_ID } from "../lib/layout/appScroll";
import { preloadDesktopLayouts } from "../lib/layout/desktopPages";
import { PageLoader } from "../routes/PageLoader";
import { APP_FRAME_ID, APP_OVERLAY_HOST_ID } from "../lib/layout/overlayHost";
import { BadgeUnlockWatcher } from "../features/gamification/components/BadgeUnlockWatcher";
import { LeagueWeekRecapWatcher } from "../features/leagues/components/LeagueWeekRecapWatcher";
import { SeasonRecapWatcher } from "../features/leagues/components/season/SeasonRecapWatcher";
import { PushPermissionWatcher } from "../features/notifications/components/PushPermissionWatcher";
import { ReviewPromptWatcher } from "../features/review/components/ReviewPromptWatcher";
import { ProLapseWatcher } from "../features/pro/components/ProLapseWatcher";
import { ReadingThemeAutoWatcher } from "../features/profile/components/ReadingThemeAutoWatcher";
import { FocusSessionHost } from "../features/focus/context/FocusSessionHost";
import { OfflineBanner } from "../components/layout/OfflineBanner";
import { BackendReachabilityProbe } from "../components/layout/BackendReachabilityProbe";

/**
 * Largura do frame. 430px é a largura real do iPhone 15 Pro Max — o topo da
 * faixa que o app já desenha para (375–430), e não um número inventado. As
 * páginas em `max-w-md` (448px) simplesmente preenchem o frame, sem segunda
 * contenção e sem sobra.
 */
const FRAME_W = "md:w-[430px]";

/**
 * A aresta do frame: fio de 1px + elevação, ambos na tinta de sombra do tema.
 *
 * Uma `border-line` aqui seria literalmente invisível — `--color-surface-alt`,
 * a cor do entorno, resolve para `var(--border-color)`, então a borda teria
 * exatamente a cor do fundo contra o qual precisa aparecer. E só o degrau de
 * cor entre entorno e frame não basta: medido nos 21 temas, ele vai a
 * **1,13:1** no Eucalipto, que é pouco para delimitar uma área.
 *
 * `--shadow-tint` resolve o impasse porque é a única tinta que cada tema já
 * define como "isto tem que aparecer sobre o meu fundo" — o fio herda essa
 * garantia em vez de disputar com a paleta.
 */
const FRAME_EDGE = "md:shadow-float md:ring-1 md:ring-[var(--shadow-tint)]";

/**
 * Casca de desktop (barra lateral + topo), só da build WEB.
 *
 * ⚠️ O teste de `VITE_NATIVE_BUILD` é escrito AQUI, e não importado: o Vite
 * troca a variável pelo literal e o Rollup dobra `"true" === "true"` dentro do
 * módulo, descartando o `import()` — e com ele o chunk inteiro de
 * `src/desktop/`. É o mesmo desenho do `adminRoutes()`, cobrado pelo mesmo
 * `mobile-postbuild.mjs`. Na web, o `lazy()` só busca o chunk quando a casca
 * é de fato montada (tela ≥ 1024px): o celular nunca o baixa.
 */
const IS_NATIVE_BUILD = import.meta.env.VITE_NATIVE_BUILD === "true";
const DesktopChrome = IS_NATIVE_BUILD ? null : lazy(() => import("../desktop/DesktopChrome"));
/**
 * Casca do VISITANTE sem conta (mock `web-desktop-visitante.html`): topo com a
 * marca e "Entrar / Criar conta grátis" + rodapé institucional. Mesmo gate da
 * de cima; os dois `lazy()` apontam para o MESMO chunk (`VisitorDesktop`).
 */
const VisitorTopBar = IS_NATIVE_BUILD ? null : lazy(() => import("../desktop/VisitorDesktop"));
const VisitorFooter = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../desktop/VisitorDesktop").then((m) => ({ default: m.VisitorFooter })));

/**
 * O frame DENTRO da casca de desktop: a mesma coluna de 430px, centrada na
 * área à direita da barra lateral e abaixo do topo. Continua sendo a casca
 * transformada com scroller interno (ver o cabeçalho) — só muda onde ela
 * fica. `--nav-h: 0px` porque ali não há BottomNav: quem se apoiava nela
 * (faixa offline, toasts) desce até a borda da coluna.
 */
const SHELL_FRAME =
  "fixed bottom-0 top-(--desktop-topbar-h) left-[calc(50%+var(--desktop-sidebar-w)/2)] -translate-x-1/2 w-[430px] shadow-float ring-1 ring-[var(--shadow-tint)]";
const SHELL_FRAME_STYLE = { "--nav-h": "0px" } as CSSProperties;

/**
 * Frame LARGO: a página tem desenho de desktop (`useWideDesktopFrame`) e ocupa a
 * área inteira ao lado da barra lateral. Continua transformado — `translateZ(0)`
 * mantém o frame como containing block dos `fixed` de dentro (sheets, toasts),
 * que é a razão de existir da casca (ver o cabeçalho). Sem aresta: não há
 * "coluna" para delimitar.
 */
const SHELL_WIDE_FRAME =
  "fixed bottom-0 top-(--desktop-topbar-h) left-(--desktop-sidebar-w) right-0 [transform:translateZ(0)]";

/** Frame do VISITANTE: sem barra lateral, a janela inteira abaixo do topo. */
const VISITOR_FRAME = "fixed bottom-0 top-(--desktop-topbar-h) left-0 right-0 [transform:translateZ(0)]";

/**
 * O corte é `md` (768px): é a primeira largura em que 430px de app deixam
 * gutter suficiente para o resultado ler como escolha e não como página
 * quebrada. Abaixo disso — celular e tablet retrato — nada muda.
 */
export function AppFrame() {
  const { pathname } = useLocation();
  const { user, isAnonymous, isLoading: isAuthLoading } = useAuth();
  const isWide = useWideViewport();
  // Lido uma vez por montagem da casca: a saída (`?desktop=0`) é do aparelho,
  // não da rota.
  const [isEnabledOnDevice] = useState(() => DesktopChrome !== null && readDesktopShellEnabled());
  // Dica da última sessão (ver `readRealSessionHint`): lida uma vez, vale só
  // enquanto o Auth não respondeu.
  const [sessionHint] = useState(() => DesktopChrome !== null && readRealSessionHint());
  const shellOn =
    DesktopChrome !== null &&
    shouldUseDesktopShell({
      isEnabledOnDevice,
      isNativeBuild: IS_NATIVE_BUILD,
      isWideViewport: isWide,
      hasRealSession: shellSessionGuess({
        hasUser: !!user,
        isAnonymous,
        isAuthLoading,
        hint: sessionHint,
      }),
      pathname,
    });
  // O Auth assentou: a dica passa a dizer o que ele disse (logout apaga).
  useEffect(() => {
    if (DesktopChrome === null || isAuthLoading) return;
    writeRealSessionHint(!!user && !isAnonymous);
  }, [isAuthLoading, user, isAnonymous]);
  // Casca ligada: baixa já os layouts de Início/Perfil/Livro/duas colunas,
  // em vez de na 1ª visita a cada um (chunks fora do precache).
  useEffect(() => {
    if (shellOn) preloadDesktopLayouts?.();
  }, [shellOn]);
  const visitorOn =
    VisitorTopBar !== null &&
    shouldUseVisitorShell({
      isEnabledOnDevice,
      isNativeBuild: IS_NATIVE_BUILD,
      isWideViewport: isWide,
      hasAnonymousSession: !!user && isAnonymous,
      pathname,
    });
  const [ownerTheme, setOwnerTheme] = useState<OwnerTheme | null>(null);
  const [focusSlot, setFocusSlot] = useState<HTMLElement | null>(null);
  // Pedidos de frame largo, CONTADOS (ver `DesktopShellValue.setWideFrame`).
  const [widePedidos, setWidePedidos] = useState(0);
  const setWideFrame = useCallback((wide: boolean) => setWidePedidos((n) => Math.max(0, n + (wide ? 1 : -1))), []);
  const wideFrame = widePedidos > 0;
  const [frameRaised, setFrameRaised] = useState(false);
  const shell = useMemo<DesktopShellValue | null>(
    () =>
      shellOn
        ? { kind: "app", focusSlot, setFocusSlot, wideFrame, setWideFrame, setFrameRaised }
        : visitorOn
          ? // Visitante: sem Foco (não há sessão de leitura) e o frame é sempre
            // largo — as três rotas têm desenho próprio.
            { kind: "visitor", focusSlot: null, setFocusSlot: () => {}, wideFrame: true, setWideFrame, ownerTheme, setOwnerTheme }
          : null,
    [shellOn, visitorOn, focusSlot, wideFrame, setWideFrame, ownerTheme],
  );
  const isVisitorShell = shell?.kind === "visitor";

  return (
    <DesktopShellContext.Provider value={shell}>
      {/* Fundo do entorno. `aria-hidden` porque é superfície, não conteúdo. */}
      <div aria-hidden className="hidden md:fixed md:inset-0 md:block md:bg-surface-alt" />

      {shell?.kind === "app" && DesktopChrome && (
        // O fallback reserva a barra lateral com a mesma cor enquanto o chunk
        // chega, para a coluna não nascer ao lado de um vão.
        <Suspense
          fallback={
            <div
              aria-hidden
              className="fixed inset-y-0 left-0 w-(--desktop-sidebar-w) border-r border-line bg-surface"
            />
          }
        >
          <DesktopChrome />
        </Suspense>
      )}
      {isVisitorShell && VisitorTopBar && (
        <Suspense fallback={<div aria-hidden className="fixed inset-x-0 top-0 h-(--desktop-topbar-h) bg-background" />}>
          <VisitorTopBar />
        </Suspense>
      )}

      <div
        id={APP_FRAME_ID}
        className={
          shell
            ? `bg-background font-body ${isVisitorShell ? VISITOR_FRAME : shell.wideFrame ? SHELL_WIDE_FRAME : SHELL_FRAME}${frameRaised ? " z-50" : ""}`
            : `min-h-dvh bg-background font-body md:fixed md:inset-y-0 md:left-1/2 md:-translate-x-1/2 ${FRAME_EDGE} ${FRAME_W}`
        }
        style={shell ? SHELL_FRAME_STYLE : undefined}
        // Marca que as folhas usam para virar diálogo (`desktop/desktopDialogs.css`).
        data-desktop-shell={shell ? "" : undefined}
      >
        {/* Quem rola. Separado da casca de propósito — ver o bloco acima.
            `overscroll-contain` impede a rolagem de encadear para o documento
            ao bater no fim, que é o que faria a página inteira "pular".
            `pb-(--focus-dock-h)`: com o Foco minimizado, o drawer de 116px
            cobre o fim de toda rota (BUG-282) — a reserva mora AQUI, no
            contêiner de todas elas, e vale 0px sem drawer. */}
        <div
          id={APP_SCROLL_ROOT_ID}
          className={
            shell
              ? // `lg:` e não cru: a casca só existe ≥ 1024px, e a guarda do
                // BUG-124 (`nativeViewportGuard.test.ts`) proíbe scroller sem
                // prefixo de largura — ele passaria a rolar no celular.
                "pb-(--focus-dock-h) lg:h-full lg:overflow-y-auto lg:overscroll-contain"
              : "pb-(--focus-dock-h) md:h-full md:overflow-y-auto md:overscroll-contain"
          }
        >
          {/* A espera do chunk da página mora AQUI, dentro do frame (05/10).
              No `Suspense` de fora (`AppRoutes`), a 1ª página `lazy()`
              escondia o AppFrame INTEIRO — barra lateral junto — e o loader
              ocupava a janela: medido 18–200 ms com cache, 2,3–3,1 s sem.
              Navegação entre páginas prontas não passa por este fallback (o
              Router troca de rota em transição e mantém a página de antes). */}
          <Suspense fallback={<PageLoader height={shell ? "100%" : undefined} />}>
            {/* Guarda as telas: o voltar mostra a de antes pronta (BUG-331). */}
            <KeepAliveOutlet />
          </Suspense>
          {isVisitorShell && VisitorFooter && (
            <Suspense fallback={null}>
              <VisitorFooter />
            </Suspense>
          )}
        </div>

        {/* Watchers de celebração. Ficam AQUI, e não no `AppRoutes`, porque
            `BadgeUnlockModal` e `PushPermissionSheet` usam `fixed` cru sem
            portal: montados fora do frame, escapariam dele. Mover é seguro
            porque a allowlist de celebração (`isSafeCelebrationRoute`) só
            contém rotas que ficam dentro do frame — nenhuma delas é landing,
            login, institucional ou /admin. O `CoinDropProvider` continua no
            `AppRoutes`: ele usa portal, então o host abaixo já o contém, e
            mantê-lo global preserva o alcance que ele tem hoje. */}
        <LeagueWeekRecapWatcher />
        <SeasonRecapWatcher />
        <BadgeUnlockWatcher />
        <PushPermissionWatcher />
        <ReviewPromptWatcher />
        <ProLapseWatcher />
        {/* Tema da leitura acompanha a leitura atual (só PRO com o tema equipado). */}
        <ReadingThemeAutoWatcher />

        {/* Modo Foco. Fica AQUI pela mesma razão que os watchers acima —
            `FocusModeModal` desenha com `fixed` cru sem portal — e não nas
            páginas, que era o BUG-164: a sessão sumia ao trocar de aba e não
            voltava ao retornar. Renderizado neste nível, ele acompanha a
            navegação inteira e o agendamento da conclusão do pomodoro para de
            morrer no unmount da página. */}
        <FocusSessionHost />

        {/* Faixa de rede (02/09; BUG-260). `fixed` acima da nav, dentro do
            frame pela mesma razão dos watchers: fora dele escaparia dos 430px.
            A sonda alimenta o estado "instável" da faixa (rede "conectada" sem
            alcançar o Firestore) e não desenha nada. */}
        <BackendReachabilityProbe />
        <OfflineBanner />

        {/* Destino dos overlays com portal. Irmão do scroller — se fosse filho
            dele, todo modal rolaria junto com o conteúdo. */}
        <div id={APP_OVERLAY_HOST_ID} />
      </div>
    </DesktopShellContext.Provider>
  );
}
